/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement, useState } from 'react';
import {
    Badge,
    Button,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    PositioningVirtualElement,
    TeachingPopover,
    TeachingPopoverBody,
    TeachingPopoverHeader,
    TeachingPopoverSurface,
    TeachingPopoverTitle,
    TeachingPopoverTrigger,
    Text,
    Tooltip,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { CalendarRegular, DeleteRegular, EditRegular, MoneyFilled, MoreHorizontal20Regular } from '@fluentui/react-icons';
import { Column, ColumnProps, Paths, Row } from '../common';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { AddEditCaseEventDialog } from '../dialogs/AddEditCaseEventDialog';
import { ConfirmationDialog } from '../dialogs/ConfirmationDialog';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { useAccess } from '../../auth/useAccess';
import { useNavigate } from 'react-router';
import { CaseEvent } from '../../data/types';
import { useCaseMilestoneService, useCaseService } from '../../data/services';
import { useMediaSizes } from '../common/Styles/useMediaSizes';
import { appTokens } from '../common/Styles/appTokens';
import { LinkButton } from './LinkButton';

const useStyles = makeStyles({
    card: {},
    eventText: {},
    casePersona: {
        userSelect: 'none',
        overflowX: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        cursor: 'pointer',
        ':hover .fui-Persona__primaryText': {
            color: appTokens.border.colors.brand,
        },
    },
    clickable: {
        cursor: 'pointer',
    },
});
export type CaseEventControlProps = {
    className?: string;
    event?: CaseEvent;
    onDelete?: (eventId: string) => void;
    onEdit?: (values: any) => Promise<void>;
    showCase?: boolean;
    showMilestone?: boolean;
};
export const CaseEventControl = (props: CaseEventControlProps) => {
    const { className, event, onDelete, onEdit, showCase = true, showMilestone } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const navTo = useNavigate();
    const mediaSizes = useMediaSizes();
    const caseService = useCaseService();
    const milestoneService = useCaseMilestoneService();

    //State
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [contextMenuTarget, setContextMenuTarget] = useState<PositioningVirtualElement | null>(null);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const controlClasses = mergeClasses(commonStyles.utility.withHiddenChildren, styles.clickable, className);

    //Access
    const canEditEvent = () => {
        return access.cases.canEditEvents(event?.caseId);
    };
    const canDeleteEvent = () => {
        return access.cases.canDeleteEvents(event?.caseId);
    };

    //Logic
    const getCase = () => caseService?.findCaseById(event?.caseId);
    const getMilestone = () => milestoneService?.findMilestone(event?.milestoneId);

    //UI Events
    const onClickCase = (e: any) => {
        e.stopPropagation();

        navTo(Paths.viewEditCase(event?.caseId as string));
    };

    const canOpenEditDialog = !!onEdit && !!event?.caseId && canEditEvent();

    const doubleClickControl = () => {
        if (!canOpenEditDialog) return;

        setEditDialogOpen(true);
    };

    const onContextMenuControl = (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        const { clientX, clientY } = e;
        // Anchor the menu to the pointer location instead of the trigger button.
        setContextMenuTarget({ getBoundingClientRect: () => new DOMRect(clientX, clientY, 0, 0) });
        setIsMenuOpen(true);
    };
    const onMenuOpenChange = (isOpen: boolean) => {
        setIsMenuOpen(isOpen);

        if (!isOpen) {
            setContextMenuTarget(null);
        }
    };

    return (
        <>
            <Column
                shadowSmall
                className={controlClasses}
                subtleBorder
                padding='pl-2 pr-3 py-2'
                height='fit-content'
                onDoubleClick={doubleClickControl}
                onContextMenu={onContextMenuControl}
                backgroundColor={appTokens.eventControl.background}
                borderLeftStyle={appTokens.eventControl.borderLeftStyle}
                borderRadius={appTokens.eventControl.borderRadius}
                noGap
            >
                <Row noWrap noGap>
                    <Column flex='1' vAlign='middle'>
                        <Text size={200} weight='regular'>
                            EVENT
                        </Text>
                    </Column>
                    <Column flex='1' align='right' vAlign='middle' padding='pb-1'>
                        <EventControlMenu
                            event={event}
                            open={isMenuOpen}
                            onOpenChange={onMenuOpenChange}
                            positioningTarget={contextMenuTarget}
                            onEdit={() => setEditDialogOpen(true)}
                            canEdit={canOpenEditDialog}
                            onDelete={!!onDelete ? () => onDelete(event?.eventId as string) : undefined}
                            canDelete={canDeleteEvent()}
                        />
                    </Column>
                </Row>

                <Row gap='small' padding='pb-2'>
                    <Column noGap flex='1 1 auto' vAlign='top' align='left' overflowX='hidden' padding='pb-2'>
                        <Tooltip
                            content={
                                <Text weight='semibold'>
                                    Description:{' '}
                                    <Text>
                                        {event?.description?.substring(0, 150)}
                                        {event?.description && event?.description.length > 150 ? '...' : ''}
                                    </Text>
                                </Text>
                            }
                            relationship='label'
                        >
                            <Text size={mediaSizes.isMobile ? 400 : 500} weight='semibold'>
                                {event?.event}
                            </Text>
                        </Tooltip>
                        {!!showCase && (
                            <Row gap='small' noWrap>
                                <Column vAlign='middle' width='auto'>
                                    <Text weight='semibold' size={300}>
                                        Case:
                                    </Text>
                                </Column>
                                <Column vAlign='top' align='left' minWidth='0px'>
                                    <LinkButton
                                        onClick={onClickCase}
                                        text={getCase()?.caseName || ''}
                                        data-testid={`task-control-case-link-${event?.caseId}`}
                                    />
                                </Column>
                            </Row>
                        )}
                        {!!showMilestone && getMilestone() && (
                            <Column>
                                <Text weight='semibold'>
                                    Milestone: <Text>{getMilestone()?.name}</Text>
                                </Text>
                            </Column>
                        )}
                    </Column>
                    <Column width='auto' align='right' vAlign='top'>
                        <DateControl date={event?.date} due={event?.due} />
                    </Column>
                </Row>

                <Column flex='1' vAlign='top' align='left' padding='pb-2'>
                    <Text weight='semibold'>
                        Description:{' '}
                        <Text>
                            {event?.description?.substring(0, 150)}
                            {event?.description && event?.description.length > 150 ? '...' : ''}
                        </Text>
                    </Text>
                </Column>
            </Column>

            {/* Rendered outside the card/menu so closing the menu popover doesn't unmount the dialog. */}
            {canOpenEditDialog && (
                <AddEditCaseEventDialog
                    onSubmit={onEdit}
                    eventToEdit={event}
                    caseId={event?.caseId as string}
                    open={editDialogOpen}
                    onOpenChange={setEditDialogOpen}
                />
            )}
        </>
    );
};

const useDateControlStyles = makeStyles({
    root: {
        position: 'relative',
        border: `1px solid ${appTokens.border.colors.subtle}`,
        padding: '0rem',
        width: 'auto',
        minWidth: '4rem',
        minHeight: '4rem',
    },
    large: {
        minWidth: '5rem',
        minHeight: '5rem',
    },
    dateText: {
        color: appTokens.foregrounds.neutral,
        padding: '0px',
        pointerEvents: 'none',
    },
    dateDueText: {
        color: appTokens.text.colors.brand,
    },
    pastText: {
        color: appTokens.text.colors.disabled,
    },
    stamp: {
        position: 'absolute',
        top: '-.5rem',
        left: '0%',
        right: '0%',
    },
    billable: {
        position: 'absolute',
        bottom: '-.5rem',
        right: '-.5rem',
    },
    money: {
        transform: 'rotate(-45deg)',
    },
});

type DateControlProps = Partial<ColumnProps> & {
    className?: string;
    date?: Date;
    due?: boolean;
    border?: boolean;
    size?: 'small' | 'medium' | 'large';
    dueStamp?: boolean;
    billable?: boolean;
};
export const DateControl = (props: DateControlProps) => {
    const { className, date, due = false, dueStamp = false, size = 'medium', billable = false, ...otherProps } = props;

    const theDate = !!date ? new Date(date) : new Date();
    const currentDate = new Date();
    const isThisYear = theDate.getFullYear() == currentDate.getFullYear();
    const isPast = !!date && theDate < currentDate;

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useDateControlStyles();
    const rootClasses = mergeClasses(styles.root, size == 'large' && styles.large, className);
    const textClasses = mergeClasses(
        commonStyles.utility.noSelect,
        styles.dateText,
        !!due && styles.dateDueText,
        isPast && styles.pastText,
    );

    //Logic
    const getMonthSize = () => {
        switch (size) {
            case 'small':
                return 100;
            case 'medium':
                return 200;
            case 'large':
                return 400;
        }
    };
    const getDaySize = () => {
        switch (size) {
            case 'small':
                return 300;
            case 'medium':
                return 500;
            case 'large':
                return 700;
        }
    };

    return (
        <Column
            className={rootClasses}
            flex='1'
            backgroundColor={appTokens.dateControl.background(isPast)}
            borderRadius='10px'
            {...otherProps}
        >
            {!!due && !!dueStamp && (
                <Column height='auto' className={styles.stamp} align='center'>
                    <Badge appearance='filled' size='small' color={isPast ? 'informative' : 'brand'}>
                        Due
                    </Badge>
                </Column>
            )}
            <Column align='center' vAlign='middle' noGap>
                <Text className={textClasses} size={getMonthSize()} weight='semibold'>
                    {theDate.toLocaleString('default', { month: 'short' })}
                </Text>
                <Text className={textClasses} size={getDaySize()} weight='bold'>
                    {theDate.getDate()}
                </Text>
                {!isThisYear ? (
                    <Text className={textClasses} size={getMonthSize()} weight='semibold'>
                        {theDate.getFullYear()}
                    </Text>
                ) : (
                    <></>
                )}
            </Column>

            {!!billable && (
                <Column height='auto' className={styles.billable} align='center'>
                    <Text style={{ fontSize: '1.3rem' }}>
                        <MoneyFilled
                            className={styles.money}
                            color={isPast ? tokens.colorNeutralForeground4 : appTokens.foregrounds.success}
                        />
                    </Text>
                </Column>
            )}
        </Column>
    );
};

const useEventPopupStyles = makeStyles({
    dueText: {
        color: tokens.colorPaletteRedForeground1,
    },
});
type EventPopupProps = {
    children?: ReactElement;
    event?: CaseEvent;
};
export const EventPopup = (props: EventPopupProps) => {
    const { children, event } = props;

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useEventPopupStyles();
    const dateTextClasses = mergeClasses(!!event?.due && styles.dueText);

    //Logic
    const getDateText = () => {
        return !!event?.date
            ? new Date(event?.date).toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
              })
            : '';
    };

    return (
        <TeachingPopover mouseLeaveDelay={0}>
            <TeachingPopoverTrigger>{children}</TeachingPopoverTrigger>
            <TeachingPopoverSurface>
                <TeachingPopoverHeader
                    className={commonStyles.utility.noSelect}
                    dismissButton={{ className: commonStyles.button.root }}
                    icon={<CalendarRegular />}
                >
                    Event
                </TeachingPopoverHeader>
                <TeachingPopoverBody style={{ alignItems: 'center' }}>
                    <TeachingPopoverTitle>{event?.event}</TeachingPopoverTitle>
                    <TeachingPopoverTitle className={dateTextClasses}>
                        {!!event?.due ? 'Due on: ' : 'On: '}
                        {getDateText()}
                    </TeachingPopoverTitle>
                    <Text>{event?.description}</Text>
                </TeachingPopoverBody>
            </TeachingPopoverSurface>
        </TeachingPopover>
    );
};


interface EventControlMenuProps {
    event?: CaseEvent;
    onEdit?: () => void;
    onDelete?: () => void;
    canEdit?: boolean;
    canDelete?: boolean;
    className?: string;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    positioningTarget?: PositioningVirtualElement | null;
}
const EventControlMenu = ({
    event,
    open,
    onOpenChange,
    positioningTarget,
    onEdit,
    onDelete,
    canEdit,
    canDelete,
    className,
}: EventControlMenuProps) => {
    const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);

    return (
        <>
            <Menu
                open={open}
                onOpenChange={(_, data) => onOpenChange?.(data.open)}
                positioning={positioningTarget ? { target: positioningTarget } : undefined}
                hasIcons
            >
                <MenuTrigger disableButtonEnhancement>
                    <Button
                        icon={<MoreHorizontal20Regular />}
                        size='small'
                        appearance='secondary'
                        className={className}
                        aria-label='Actions'
                        onClick={(e: any) => e.stopPropagation()}
                        data-testid='event-actions-menu-trigger'
                    />
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        {!!onEdit && !!canEdit && (
                            <MenuItem
                                icon={<EditRegular />}
                                onClick={onEdit}
                                data-testid={`case-event-edit-${event?.eventId}`}
                            >
                                Edit Event
                            </MenuItem>
                        )}

                        {!!onDelete && !!canDelete && (
                            <MenuItem
                                icon={<DeleteRegular />}
                                onClick={() => setIsDeleteOpen(true)}
                                data-testid={`case-event-delete-${event?.eventId}`}
                            >
                                Delete Event
                            </MenuItem>
                        )}
                    </MenuList>
                </MenuPopover>
            </Menu>

            {/* Rendered outside the Menu so closing the menu popover doesn't unmount the dialog. */}
            {!!onDelete && !!canDelete && (
                <ConfirmationDialog
                    title='Confirm Delete'
                    icon={<DeleteRegular />}
                    text='Are you sure you want to delete this event?'
                    open={isDeleteOpen}
                    onOpenChange={setIsDeleteOpen}
                    onConfirm={onDelete}
                />
            )}
        </>
    );
};